import React, { useMemo, useState } from 'react';
import { playSuccessSound, playFailureSound } from '../utils/materialUtils';

const OrderWordsExercise = ({
  segment,
  answerIds = [],
  status = null,
  onAnswerChange,
  onStatusChange,
  silent = false,
  readOnly = false,
}) => {
  const tokens = useMemo(
    () => segment.tokens || [],
    [segment.tokens]
  );

  const tokenMap = useMemo(() => {
    const map = new Map();
    tokens.forEach((token) => map.set(token.id, token.text));
    return map;
  }, [tokens]);

  const correctOrder = useMemo(
    () => segment.correctOrder || tokens.map((token) => token.id),
    [segment.correctOrder, tokens]
  );

  const poolLayoutIds = useMemo(
    () => segment.poolLayoutIds || tokens.map((token) => token.id),
    [segment.poolLayoutIds, tokens]
  );

  const [draggedId, setDraggedId] = useState(null);

  const isLocked = status === 'correct' || readOnly;
  const allPlaced = answerIds.length === tokens.length;

  const moveToAnswer = (tokenId) => {
    if (isLocked || answerIds.includes(tokenId)) return;
    onAnswerChange([...answerIds, tokenId]);
    if (status === 'incorrect') {
      onStatusChange(null);
    }
  };

  const moveToPool = (tokenId) => {
    if (isLocked || !answerIds.includes(tokenId)) return;
    onAnswerChange(answerIds.filter((id) => id !== tokenId));
    if (status === 'incorrect') {
      onStatusChange(null);
    }
  };

  const handleDragStart = (event, tokenId) => {
    if (isLocked) {
      event.preventDefault();
      return;
    }
    setDraggedId(tokenId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', tokenId);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnAnswer = (event) => {
    event.preventDefault();
    const tokenId = event.dataTransfer.getData('text/plain') || draggedId;
    if (!tokenId) return;

    if (answerIds.includes(tokenId)) {
      setDraggedId(null);
      return;
    }

    moveToAnswer(tokenId);
    setDraggedId(null);
  };

  const handleDropOnPoolSlot = (event, slotTokenId) => {
    event.preventDefault();
    const tokenId = event.dataTransfer.getData('text/plain') || draggedId;
    if (!tokenId) return;

    if (tokenId === slotTokenId && answerIds.includes(tokenId)) {
      moveToPool(tokenId);
    } else if (!answerIds.includes(tokenId) && tokenId === slotTokenId) {
      moveToAnswer(tokenId);
    }

    setDraggedId(null);
  };

  const renderAnswerChip = (tokenId) => {
    const text = tokenMap.get(tokenId);

    return (
      <button
        key={`answer-${tokenId}`}
        type="button"
        className={`order-word-chip ${status || ''}`}
        draggable={!isLocked}
        disabled={isLocked}
        onClick={() => moveToPool(tokenId)}
        onDragStart={(event) => handleDragStart(event, tokenId)}
        onDragEnd={handleDragEnd}
      >
        {text}
      </button>
    );
  };

  const renderPoolSlot = (tokenId) => {
    const text = tokenMap.get(tokenId);
    const isUsed = answerIds.includes(tokenId);

    if (isUsed) {
      return (
        <span
          key={`ghost-${tokenId}`}
          className="order-word-ghost"
          onDragOver={handleDragOver}
          onDrop={(event) => handleDropOnPoolSlot(event, tokenId)}
        >
          {text}
        </span>
      );
    }

    return (
      <button
        key={`pool-${tokenId}`}
        type="button"
        className={`order-word-chip ${status || ''}`}
        draggable={!isLocked}
        disabled={isLocked}
        onClick={() => moveToAnswer(tokenId)}
        onDragStart={(event) => handleDragStart(event, tokenId)}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={(event) => handleDropOnPoolSlot(event, tokenId)}
      >
        {text}
      </button>
    );
  };

  const handleCheck = () => {
    if (isLocked || !allPlaced) return;

    const isCorrect = answerIds.every((id, index) => id === correctOrder[index]);

    if (isCorrect) {
      onStatusChange('correct');
      if (!silent) playSuccessSound();
      return;
    }

    onStatusChange('incorrect');
    if (!silent) playFailureSound();
  };

  return (
    <div className={`order-exercise ${status || ''}`}>
      <div
        className={`order-answer ${status || ''}`}
        onDragOver={handleDragOver}
        onDrop={handleDropOnAnswer}
      >
        {answerIds.length === 0 ? (
          <span className="order-answer-placeholder">Составьте предложение</span>
        ) : (
          answerIds.map((tokenId) => renderAnswerChip(tokenId))
        )}
      </div>

      <div className="order-pool-row">
        <div className="order-pool">
          {poolLayoutIds.map((tokenId) => renderPoolSlot(tokenId))}
        </div>
        <button
          type="button"
          className="order-check-btn"
          disabled={!allPlaced || isLocked}
          onClick={handleCheck}
        >
          Проверить
        </button>
      </div>
    </div>
  );
};

export default OrderWordsExercise;
